import React, { useEffect, useState } from "react";
import { useAppContext } from "context";
import { action, API, controller } from "../../../api/api";
import { useFormatPrice } from "hooks";
import { useTranslation } from "react-i18next";
import {
  ReceiptLongRounded,
  AttachMoneyRounded,
} from "@mui/icons-material";
import { CircularProgress } from "@mui/material";

const StatCard = ({ icon, label, value, sub }) => (
  <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-3 min-w-0">
    <div className="flex items-center justify-center w-9 h-9 rounded-full bg-white shadow-sm shrink-0">
      {icon}
    </div>
    <div className="grid gap-[1px] min-w-0">
      <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wide truncate">
        {label}
      </span>
      <span className="text-[15px] font-bold text-gray-700 truncate">
        {value}
      </span>
      {sub && (
        <span className="text-[11px] text-gray-400 truncate">{sub}</span>
      )}
    </div>
  </div>
);

// Own-takings summary. Which of the two windows appear is the SERVER's decision
// (MySalesOverviewAccess.Today / .ThisMonth): a block the user may not see is never
// computed, so its numbers are absent from the response rather than merely hidden
// here. We render from canViewToday/canViewMonth and never infer from the values —
// a granted-but-zero day must still show "0", not vanish.
const SalesCounts = ({ open }) => {
  const formatPrice = useFormatPrice();
  const { authUser, selCompany } = useAppContext();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState();

  useEffect(() => {
    if (authUser && open && selCompany) {
      setLoading(true);
      API(controller.Sell + action.CurrentMonthAndTodaySaleByUser).getById(
        selCompany,
        (res) => {
          // 403 = the whole section is not granted; leave `accounts` unset so the
          // component renders nothing at all.
          res && res.status === 200 && setAccounts({ ...res.result });
          setLoading(false);
        }
      );
    }
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser, open, selCompany]);

  if (loading) {
    return (
      <div className="flex justify-center py-6 border-b">
        <CircularProgress size={24} />
      </div>
    );
  }

  if (!accounts) return null;
  if (!accounts.canViewToday && !accounts.canViewMonth) return null;

  // One labelled window (Today / This Month) — invoice count + takings.
  const Period = ({ label, invoices, sales }) => (
    <div className="grid gap-2">
      <span className="text-[12px] font-semibold text-gray-400 uppercase tracking-wider px-1">
        {label}
      </span>
      <div className="grid grid-cols-2 gap-2">
        <StatCard
          icon={
            <ReceiptLongRounded
              sx={{ fontSize: 18 }}
              className="text-blue-500"
            />
          }
          label={t("totalInvoices")}
          value={(invoices || 0).toLocaleString("en-US")}
        />
        <StatCard
          icon={
            <AttachMoneyRounded
              sx={{ fontSize: 18 }}
              className="text-green-600"
            />
          }
          label={t("totalSales")}
          value={formatPrice(sales)}
        />
      </div>
    </div>
  );

  return (
    <div className="grid gap-4 p-4 border-b">
      {/* Section Header */}
      <div className="flex items-center gap-1.5 px-1">
        <span className="text-[13px] font-semibold text-gray-500">
          {t("mySalesOverview")}
        </span>
      </div>

      {accounts.canViewToday && (
        <Period
          label={t("today")}
          invoices={accounts.totalSoldToday}
          sales={accounts.todaySales}
        />
      )}

      {accounts.canViewMonth && (
        <Period
          label={t("thisMonth")}
          invoices={accounts.totalSoldThisMonth}
          sales={accounts.thisMonthSales}
        />
      )}
    </div>
  );
};

export default SalesCounts;
